import * as React from 'react';

const BOUNDARY_COUNT = 0;
const SIBLING_COUNT = 2;

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

// Calculations lifted from material-ui's usePagination hook
export function usePagination(count, page) {
  const startPages = React.useMemo(
    () => range(1, Math.min(BOUNDARY_COUNT + 1, count)),
    [count],
  );
  const endPages = React.useMemo(
    () => range(Math.max(count - BOUNDARY_COUNT, BOUNDARY_COUNT + 2), count),
    [count],
  );

  const siblingsStart = React.useMemo(
    () =>
      Math.max(
        Math.min(
          // Natural start
          page - SIBLING_COUNT,
          // Lower boundary when page is high
          count - BOUNDARY_COUNT - SIBLING_COUNT * 2 - 2,
        ),
        // Greater than startPages
        BOUNDARY_COUNT + 3,
      ),
    [count, page],
  );

  const siblingsEnd = React.useMemo(
    () =>
      Math.min(
        Math.max(
          // Natural end
          page + SIBLING_COUNT,
          // Upper boundary when page is low
          BOUNDARY_COUNT + SIBLING_COUNT * 2 + 3,
        ),
        // Less than endPages
        endPages[0] - 2,
      ),
    [endPages, page],
  );

  // Basic list of items to render
  // e.g. itemList = [ 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10]
  const getItemList = React.useCallback(
    (newCount) => {
      return [
        ...startPages,

        // Start ellipsis
        // eslint-disable-next-line no-nested-ternary
        ...(siblingsStart > BOUNDARY_COUNT + 3
          ? ['start-ellipsis']
          : 2 + BOUNDARY_COUNT < newCount - BOUNDARY_COUNT - 1
            ? [2 + BOUNDARY_COUNT]
            : []),

        // Sibling pages
        ...range(siblingsStart, siblingsEnd),

        // End ellipsis
        // eslint-disable-next-line no-nested-ternary
        ...(siblingsEnd < newCount - BOUNDARY_COUNT - 2
          ? ['end-ellipsis']
          : newCount - BOUNDARY_COUNT - 1 > BOUNDARY_COUNT + 1
            ? [newCount - BOUNDARY_COUNT - 1]
            : []),

        ...endPages,
      ];
    },
    [startPages, endPages, siblingsEnd, siblingsStart],
  );

  return React.useMemo(
    () => getItemList(count, page),
    [count, page, getItemList],
  );
}
